import { type RefObject, useEffect, useRef, useState } from 'react';

import type * as echarts from 'echarts/core';

export type ChartZoom = {
  pick: (first: number, last: number) => void;
  scale: (count: number, anchor: number) => void;
  reset: () => void;
};

type Gesture = Event & { scale: number; clientX: number; clientY: number };

const DRAG_MIN_PX = 6;
const WHEEL_DOUBLING = 100;

function axisAt(instance: echarts.ECharts, x: number): { count: number; anchor: number } | undefined {
  const count = (instance.getOption().xAxis as Array<{ data?: Array<unknown> }> | undefined)?.[0]?.data?.length ?? 0;
  if (count < 2) return undefined;
  const first = instance.convertToPixel({ xAxisIndex: 0 }, 0) as number;
  const last = instance.convertToPixel({ xAxisIndex: 0 }, count - 1) as number;
  return { count, anchor: Math.min(1, Math.max(0, (x - first) / (last - first))) };
}

export default function useChartZoom(host: RefObject<HTMLDivElement>, chart: RefObject<echarts.ECharts | undefined>, zoom?: ChartZoom) {
  const [band, setBand] = useState<{ left: number; width: number }>();
  const latest = useRef(zoom);
  latest.current = zoom;
  const enabled = Boolean(zoom);
  useEffect(() => {
    const node = host.current;
    if (!node || !enabled) return undefined;
    const local = (event: { clientX: number; clientY: number }) => {
      const rect = node.getBoundingClientRect();
      return [event.clientX - rect.left, event.clientY - rect.top] as const;
    };
    const inPlot = (x: number, y: number) => Boolean(chart.current?.containPixel('grid', [x, y]));
    const at = (x: number) => (chart.current ? axisAt(chart.current, x) : undefined);
    const indexAt = (x: number) => {
      const found = at(x);
      return found ? Math.round(found.anchor * (found.count - 1)) : undefined;
    };

    let drag: { x: number; pointer: number } | undefined;
    const touches = new Map<number, number>();
    let pinch: { distance: number; count: number; anchor: number } | undefined;
    const pinchFrom = () => {
      const [left, right] = [...touches.values()];
      const found = at((left + right) / 2);
      pinch = found && Math.abs(right - left) > 0 ? { distance: Math.abs(right - left), count: found.count, anchor: found.anchor } : undefined;
    };

    const down = (event: PointerEvent) => {
      const [x, y] = local(event);
      if (event.pointerType === 'touch') {
        touches.set(event.pointerId, x);
        if (touches.size === 2) pinchFrom();
        return;
      }
      if (event.pointerType !== 'mouse' || event.button !== 0 || !inPlot(x, y)) return;
      drag = { x, pointer: event.pointerId };
      node.setPointerCapture(event.pointerId);
      event.preventDefault();
    };
    const move = (event: PointerEvent) => {
      const [x] = local(event);
      if (event.pointerType === 'touch' && touches.has(event.pointerId)) {
        touches.set(event.pointerId, x);
        if (!pinch || touches.size !== 2) return;
        const [left, right] = [...touches.values()];
        latest.current?.scale(Math.round((pinch.count * pinch.distance) / Math.max(1, Math.abs(right - left))), pinch.anchor);
        return;
      }
      if (drag && event.pointerId === drag.pointer && Math.abs(x - drag.x) >= DRAG_MIN_PX) setBand({ left: Math.min(x, drag.x), width: Math.abs(x - drag.x) });
    };
    const up = (event: PointerEvent) => {
      if (touches.delete(event.pointerId)) {
        pinch = undefined;
        return;
      }
      if (!drag || event.pointerId !== drag.pointer) return;
      const [x] = local(event);
      const start = drag.x;
      drag = undefined;
      setBand(undefined);
      if (event.type === 'pointercancel' || Math.abs(x - start) < DRAG_MIN_PX) return;
      const first = indexAt(Math.min(x, start));
      const last = indexAt(Math.max(x, start));
      if (first !== undefined && last !== undefined && last > first) latest.current?.pick(first, last);
    };

    let wheeled = 0;
    const wheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      const [x, y] = local(event);
      const found = inPlot(x, y) ? at(x) : undefined;
      if (!found) return;
      event.preventDefault();
      wheeled += Math.max(-WHEEL_DOUBLING, Math.min(WHEEL_DOUBLING, event.deltaY)) / WHEEL_DOUBLING;
      const count = Math.round(found.count * 2 ** wheeled);
      if (count === found.count) return;
      wheeled = 0;
      latest.current?.scale(count, found.anchor);
    };

    let gesture: { count: number; anchor: number } | undefined;
    const gestureStart = (event: Event) => {
      const [x, y] = local(event as Gesture);
      gesture = inPlot(x, y) ? at(x) : undefined;
      if (gesture) event.preventDefault();
    };
    const gestureChange = (event: Event) => {
      if (!gesture) return;
      event.preventDefault();
      latest.current?.scale(Math.round(gesture.count / (event as Gesture).scale), gesture.anchor);
    };
    const gestureEnd = () => {
      gesture = undefined;
    };

    const doubleClick = (event: MouseEvent) => {
      const [x, y] = local(event);
      if (inPlot(x, y)) latest.current?.reset();
    };

    const listeners: Array<[string, (event: never) => void, AddEventListenerOptions?]> = [
      ['pointerdown', down],
      ['pointermove', move],
      ['pointerup', up],
      ['pointercancel', up],
      ['wheel', wheel, { passive: false }],
      ['gesturestart', gestureStart, { passive: false }],
      ['gesturechange', gestureChange, { passive: false }],
      ['gestureend', gestureEnd],
      ['dblclick', doubleClick],
    ];
    for (const [type, listener, options] of listeners) node.addEventListener(type, listener as EventListener, options);
    return () => {
      for (const [type, listener] of listeners) node.removeEventListener(type, listener as EventListener);
    };
  }, [chart, enabled, host]);
  return band;
}
